import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/site/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lock, Download, X } from "lucide-react";

export default function ModelDetail() {
  const { modelId } = useParams();
  const [data, setData] = useState(null);
  const [lightbox, setLightbox] = useState(null); // {url}
  const [busyId, setBusyId] = useState(null);
  const { user, refresh } = useAuth();
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get(`/models/${modelId}`).then(r => setData(r.data));
  }, [modelId]);
  useEffect(() => { load(); }, [load]);

  const unlock = async (img) => {
    if (!user) { nav("/login"); return; }
    const gal = data?.galleries.find(g => g.images?.some(i => i.image_id === img.image_id));
    const idx = gal ? gal.images.findIndex(i => i.image_id === img.image_id) : -1;
    const prev = gal && idx > 0 ? gal.images[idx-1] : null;
    const next = gal && idx >= 0 && idx < gal.images.length-1 ? gal.images[idx+1] : null;
    const hires = img.hires_url || img.url;
    const finalUrl = hires.startsWith("/") ? hires : hires;
    if (img.unlocked) { setLightbox({ url: finalUrl, prev, next }); return; }
    const cost = img.credit_cost || 1;
    if ((user.credits || 0) < cost) { toast.error("Insufficient credits"); nav("/pricing"); return; }
    setBusyId(img.image_id);
    try {
      await api.post(`/images/${img.image_id}/unlock`);
      await refresh();
      toast.success(`Unlocked — ${cost} credit${cost>1?"s":""} spent`);
      load();
      setLightbox({ url: finalUrl, prev, next });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not unlock");
    } finally { setBusyId(null); }
  };

  const downloadGallery = async (gid) => {
    if (!user) { nav("/login"); return; }
    if ((user.credits || 0) < 20) { toast.error("Need 20 credits"); nav("/pricing"); return; }
    if (!window.confirm("Download entire gallery as ZIP for 20 credits?")) return;
    try {
      const { data } = await api.post(`/galleries/${gid}/download`);
      await refresh();
      toast.success("Preparing your zip…");
      load();
      // Trigger download
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`;
      window.location.href = url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (!data) return <Layout><div className="p-24 text-center overline">Loading…</div></Layout>;
  const { model, galleries } = data;

  return (
    <Layout>
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="aspect-[3/4] overflow-hidden">
              <img src={model.profile_photo} alt={model.name} className="w-full h-full object-cover grayscale"/>
            </div>
          </div>
          <div className="md:col-span-7 md:pt-8">
            <div className="overline mb-4">Model</div>
            <h1 className="font-serif-editorial text-6xl md:text-8xl tracking-tighter mb-6" data-testid="model-name">{model.name}</h1>
            <p className="text-neutral-400 max-w-lg leading-relaxed">{model.bio}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {model.tags.map(t => <span key={t} className="ribbon">{t}</span>)}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 space-y-24">
        {galleries.map(g => (
          <div key={g.gallery_id}>
            <div className="flex items-end justify-between mb-8 border-b border-white/10 pb-4">
              <div>
                <div className="overline mb-2">{g.status}</div>
                <h2 className="font-serif-editorial text-4xl tracking-tighter">{g.title}</h2>
                <p className="text-neutral-500 text-sm mt-2 max-w-xl">{g.description}</p>
              </div>
              <button onClick={() => downloadGallery(g.gallery_id)} className="btn-ghost" data-testid={`download-gallery-${g.gallery_id}`}>
                <Download size={14}/> Full Gallery · 20¢
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 stagger">
              {(g.images || []).map((img, i) => {
                const cost = img.credit_cost || 1;
                return (
                <button key={img.image_id} onClick={() => unlock(img)}
                  className="relative aspect-[3/4] overflow-hidden group thumb-hover"
                  data-testid={`image-tile-${g.gallery_id}-${i}`}>
                  <img src={img.url} alt=""
                    className={`w-full h-full object-cover ${img.unlocked ? "thumb-unlocked" : "thumb-locked"}`}/>
                  {!img.unlocked && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 group-hover:bg-black/30 transition-colors">
                      {cost > 1 && (
                        <div className="absolute top-3 right-3 ribbon !py-1 !px-2 !text-[9px] bg-white text-black border-white">HERO</div>
                      )}
                      <Lock size={20} className="text-white/80 mb-3"/>
                      <div className="ribbon" data-testid={`image-cost-${g.gallery_id}-${i}`}>
                        {busyId === img.image_id ? "..." : `Unlock · ${cost}¢`}
                      </div>
                    </div>
                  )}
                </button>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {lightbox && (
        <div className="fixed inset-0 bg-black/95 z-[200] flex items-center justify-center p-6" data-testid="lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-6 right-6 text-white/70 hover:text-white z-10" data-testid="lightbox-close"><X size={28}/></button>
          {lightbox.prev && (
            <button onClick={() => unlock(lightbox.prev)} className="absolute left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-4xl z-10" data-testid="lightbox-prev">‹</button>
          )}
          {lightbox.next && (
            <button onClick={() => unlock(lightbox.next)} className="absolute right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-4xl z-10" data-testid="lightbox-next">›</button>
          )}
          <img src={lightbox.url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${lightbox.url}` : lightbox.url} alt="" className="max-h-full max-w-full object-contain"/>
        </div>
      )}
    </Layout>
  );
}
